/* eslint-disable react-hooks/exhaustive-deps */
import { Button, ButtonPrimary } from '@/components/button'
import { ArrowBack, ArrowDropdown } from '@/components/icons'
import { AutocompleteAddress, AutocompleteLocationInput, BirthdayDateInput, TextInput } from '@/components/input'
import { SelectCountryModal } from '@/components/modal'
import { signupRegisterClick, eventTracker } from '@/config/trackingFunctions'
import { duprCountries } from '@/constants/Countries'
import { ROUTE_CREATE_PASSWORD, ROUTE_DASHBOARD, ROUTE_SIGNUP } from '@/constants/Routes'
import { useAppSelector } from '@/hooks/useRedux'
import { authSelector } from '@/pages/auth/selectors'
import { saveAddress, useClaimDetail } from '@/services'
import { redeemSignup } from '@/services/auth'
import { Country, Gender } from '@/types'
import { describeLocationSaveFailure } from '@/utils/locationSaveError'
import { createSignUpStep1ValidationSchema } from '@/utils/schema/SignUpStep1ValidationSchema'
import { yupResolver } from '@hookform/resolvers/yup'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { TRACKING_TYPES, PAGE_NAMES } from '@/constants/TrackingConstants'
import { UAParser } from 'ua-parser-js'
import { SignUpMobile } from '@/pages/auth/pages/sign-up-mobile'
import { useRemoteConfig } from '@/hooks/useRemoteConfig'

type FormValues = {
  firstName: string
  lastName: string
  birthDate: string
  marketingOptIn?: boolean
  phoneOptIn?: boolean
  termsOfService: boolean
  phoneData: {
    phoneNumber: string
    isoCode: string
    dialCode: string
  }
  email: string
  confirmEmail: string
  address: AutocompleteAddress
}

export const SignUp = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const { token } = useAppSelector(authSelector)
  const { device } = UAParser()
  const isMobile = device.is('mobile')
  const { getBoolean, isReady } = useRemoteConfig()

  const enableMobileSplash = getBoolean('enableMobileSignupSplash', false)
  const requirePhoneNumberOnSignup = getBoolean('requirePhoneNumberOnSignup', false)

  const [selectedCountry, setSelectedCountry] = useState<Country>(duprCountries[0])
  const [gender, setGender] = useState<Gender>(Gender.MALE)
  const [isOpen, setIsOpen] = useState(false)
  const [ageError, setAgeError] = useState<string | undefined>(undefined)
  // Age-verified email-first signup (set by the redeem below): support recorded parental consent on a
  // pre-created account, so the under-13 block is skipped and the stored name/birthdate are locked.
  // The server forces those values regardless -- the lock is UX, not security. False otherwise.
  const [ageVerified, setAgeVerified] = useState<boolean>(false)
  const [lockedFields, setLockedFields] = useState<Array<keyof FormValues>>([])

  const { data: playerData } = useClaimDetail(location.state?.playerId)

  const toggleModal = () => setIsOpen(!isOpen)

  const defaultValues: FormValues = {
    firstName: '',
    lastName: '',
    birthDate: '',
    marketingOptIn: false,
    phoneOptIn: false,
    termsOfService: false,
    phoneData: {
      phoneNumber: '',
      isoCode: '',
      dialCode: '',
    },
    email: '',
    confirmEmail: '',
    address: {
      description: '',
      placeId: '',
    },
  }

  const validationSchema = createSignUpStep1ValidationSchema(t, requirePhoneNumberOnSignup)

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    control,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues,
    mode: 'onSubmit',
    resolver: yupResolver(validationSchema as any),
    context: { ageVerified },
  })

  const onGoBack = () => navigate(-1)

  // Email-first landing: when this form is reached by opening a signup link (/signup/:token), redeem
  // the single-use token for the stage-2 finalize token and the verified email. The email is then
  // locked to what the link proved -- the server rejects a mismatch -- and the finalize token is
  // carried into the create-account call. A claim (no :token) skips all of this.
  const { token: signupLinkToken } = useParams()
  const [finalizeToken, setFinalizeToken] = useState<string | null>(null)
  const [redeeming, setRedeeming] = useState<boolean>(!!signupLinkToken)
  const [redeemError, setRedeemError] = useState<string | undefined>(undefined)

  useEffect(() => {
    if (!signupLinkToken) return
    let active = true
    redeemSignup(signupLinkToken)
      .then((res) => {
        if (!active) return
        setFinalizeToken(res.finalizeToken)
        setValue('email', res.email)
        setValue('confirmEmail', res.email)
        if (res.ageVerified) {
          setAgeVerified(true)
          const prefill = res.prefill
          if (prefill) {
            const [first = '', ...rest] = prefill.fullName.trim().split(/\s+/)
            const firstName = prefill.firstName || first
            const lastName = prefill.lastName || rest.join(' ')
            setValue('firstName', firstName)
            setValue('lastName', lastName)
            setValue('birthDate', prefill.birthdate)
            if (prefill.gender) setGender(Gender[prefill.gender])
            // Only lock what was actually filled, so a missing stored value can't strand the form.
            const locked: Array<keyof FormValues> = []
            if (firstName) locked.push('firstName')
            if (lastName) locked.push('lastName')
            if (prefill.birthdate) locked.push('birthDate')
            setLockedFields(locked)
          }
        }
        setRedeeming(false)
      })
      .catch((err: any) => {
        if (!active) return
        setRedeemError(
          err?.response?.data?.message ||
            t('auth.signup.linkInvalid', 'This signup link is invalid or has expired. Please start again.')
        )
        setRedeeming(false)
      })
    return () => {
      active = false
    }
  }, [signupLinkToken])

  const onChangeCountry = (country: Country) => {
    setSelectedCountry(country)
    setValue('phoneData.isoCode', selectedCountry.isoCode)
    setValue('phoneData.dialCode', selectedCountry.dialCode)
  }

  const onChangePhoneNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
    const regex = /^[0-9]*$/
    if (regex.test(e.target.value)) {
      setValue('phoneData.phoneNumber', e.target.value)
    }
  }

  const checkAge = (birthDate: string) => {
    // Age-verified signups are exempt from the under-13 block (see the redeem above).
    if (ageVerified) {
      setAgeError(undefined)
      return true
    }
    const today = moment()
    const birthMoment = moment(birthDate)
    const age = today.diff(birthMoment, 'years')

    if (age < 13) {
      setAgeError(t('common.birthDateMin'))
      return false
    }

    setAgeError(undefined)
    return true
  }

  const handleBirthdateChange = (date: Date | null) => {
    if (date) {
      const formattedDate = moment(date).format('YYYY-MM-DD')
      setValue('birthDate', formattedDate, {
        shouldDirty: true,
      })
      checkAge(formattedDate)
    }
  }

  const onSubmit = handleSubmit(async (data) => {
    const fullName = `${data.firstName.trim()} ${data.lastName.trim()}`
    const marketingOptIn = data.marketingOptIn
    let addressId: number | undefined
    if (data.address && data.address.placeId) {
      // The API refuses a place with no city (a country on its own) with a 400; without this the
      // rejection escaped the submit and the form sat there with no message (ENG-514).
      try {
        const result = await saveAddress({ placeId: data.address.placeId })
        addressId = result.result.id
      } catch (error: unknown) {
        setError('address.description', { type: 'server', message: describeLocationSaveFailure(error, t) })
        return
      }
    }
    if (!checkAge(data.birthDate)) {
      return
    }
    // Duplicate-email detection happens on the signup call itself (the create-password step).
    // The old pre-flight check against /auth/email/v1.0/exists was an account-enumeration oracle
    // and that endpoint no longer exists.
    const accData = {
      addressId: addressId,
      birthdate: moment(data.birthDate).format('YYYY-MM-DD'),
      email: data.email,
      fullName: fullName || '',
      gender: gender,
      marketingOptIn: marketingOptIn,
      termsOfService: data.termsOfService,
      phone: data.phoneData.phoneNumber ? `${data.phoneData.dialCode}${data.phoneData.phoneNumber}` : '',
      isoCode: data.phoneData.isoCode,
      ...(playerData?.id && {
        userId: playerData.id,
      }),
      // The email-first finalize token: required for a genuine new signup (proves the email),
      // absent for a claim (playerData present), which the server does not gate on it.
      ...(finalizeToken && { signupToken: finalizeToken }),
    }
    navigate(ROUTE_CREATE_PASSWORD, {
      state: {
        accData: accData,
        idClaim: playerData ? true : false,
      },
    })
    signupRegisterClick()
    eventTracker(TRACKING_TYPES.SIGN_UP_REGISTER, {
      'Page Name': PAGE_NAMES.SIGN_UP,
      'Sub Page Ref': PAGE_NAMES.SIGN_UP_STEP1,
    })
  })

  useEffect(() => {
    if (playerData) {
      const names = playerData.fullname?.split(' ')
      const firstName = names?.shift()
      const lastName = names?.join(' ')

      setValue('firstName', firstName || '', {
        shouldDirty: true,
        shouldValidate: true,
      })
      setValue('lastName', lastName || '', {
        shouldDirty: true,
        shouldValidate: true,
      })

      setGender(playerData.gender)
    }
  }, [playerData])

  useEffect(() => {
    if (token) {
      navigate(ROUTE_DASHBOARD, { relative: 'path', replace: true })
    }
  }, [token])

  useEffect(() => {
    setValue('phoneData.isoCode', selectedCountry.isoCode)
    setValue('phoneData.dialCode', selectedCountry.dialCode)
  }, [selectedCountry])

  useEffect(() => {
    if (token) {
      navigate(ROUTE_DASHBOARD, { relative: 'path', replace: true })
    }
  }, [token])

  // Email-first landing: redeem in flight, or a spent/invalid link.
  if (redeeming) {
    return (
      <div className="w-screen h-screen bg-white flex justify-center items-center">
        <p className="text-navy-900 text-sm">{t('common.loading', 'Loading…')}</p>
      </div>
    )
  }
  if (redeemError) {
    return (
      <div className="w-screen h-screen bg-white flex flex-col gap-y-6 justify-center items-center text-center px-4">
        <p className="text-navy-900 font-bold text-xl">{redeemError}</p>
        <Link to={ROUTE_SIGNUP} className="text-azul underline">
          {t('auth.signup.startOver', 'Start again')}
        </Link>
      </div>
    )
  }

  // The mobile splash is its own form without the email-first plumbing, so the signup-link landing
  // always uses the standard form (which locks the email and carries the finalize token).
  if (isMobile && !signupLinkToken) {
    // Wait for the remote config to be ready. Returning null here
    // prevents showing the desktop UI on a mobile device while loading.
    if (!isReady) {
      return null
    }

    if (enableMobileSplash) {
      return <SignUpMobile />
    }
  }

  return (
    <>
      <div className="w-screen h-auto md:h-screen bg-white flex justify-center items-center">
        <div className="flex flex-col gap-y-8 md:gap-y-14 w-full px-4 pb-4 md:px-0 md:pb-0 md:w-[720px]">
          <div className="flex relative flex-col md:flex-row md:items-center lg:pl-4 gap-y-4 mt-8 md:mt-0">
            <Button onClick={onGoBack} className="hover:opacity-50 flex lg:absolute md:-left-12">
              <ArrowBack className="text-navy-900" />
            </Button>
            <h1 className="text-2xl xs:text-3xl font-bold text-navy-900 pl-0 md:pl-4 lg:pl-0">
              {t('auth.createAccount')}
            </h1>
          </div>
          <div className="flex flex-col gap-y-4">
            <div className="flex flex-col gap-y-4">
              <div className="flex flex-col md:flex-row gap-y-6 md:gap-x-4">
                <TextInput
                  className="!flex-1"
                  placeholder={t('common.firstNamePlaceHolder')}
                  labelClassName="!text-navy-900"
                  label={t('common.firstName')}
                  error={errors?.firstName?.message}
                  readOnly={lockedFields.includes('firstName')}
                  {...register('firstName')}
                />
                <TextInput
                  className="!flex-1"
                  placeholder={t('common.lastNamePlaceHolder')}
                  labelClassName="!text-navy-900"
                  label={t('common.lastName')}
                  error={errors?.lastName?.message}
                  readOnly={lockedFields.includes('lastName')}
                  {...register('lastName')}
                />
              </div>
              <div className="flex flex-col md:flex-row gap-y-6 md:gap-x-4">
                <div className="flex-1">
                  <BirthdayDateInput
                    label={t('common.birthDate')}
                    placeholderText={t('common.birthDatePlaceHolder')}
                    containerClassName="w-full"
                    labelClassName="!text-navy-900"
                    selected={watch('birthDate')}
                    onChange={handleBirthdateChange}
                    error={errors.birthDate?.message || ageError}
                    readOnly={lockedFields.includes('birthDate')}
                  />
                  {ageError && !ageVerified && (
                    <div className="mt-2 text-red-500 text-sm">
                      <Link
                        to="https://dupr.zendesk.com/hc/en-us/requests/new?ticket_form_id=31081064080148"
                        className="block mt-1 text-primary-500 font-semibold hover:underline transition-all"
                      >
                        {t('common.under13AccountCreationForm')} →
                      </Link>
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <Controller
                    name="phoneData.phoneNumber"
                    control={control}
                    render={() => (
                      <div className="flex flex-col gap-y-2 ">
                        <span className="text-sm font-semibold text-navy-900">
                          {t('common.phone')}{' '}
                          {!requirePhoneNumberOnSignup && (
                            <span className="text-xs text-navy-900 text-opacity-50 font-normal">
                              {t('common.optional')}
                            </span>
                          )}
                        </span>
                        <div
                          className={`flex gap-x-3 border p-[7px] rounded-full ${
                            errors.phoneData?.phoneNumber ? 'border-red-500' : 'border-navy-900 border-opacity-20'
                          }`}
                        >
                          <button
                            className="flex items-center justify-center gap-x-2 bg-slate-100 rounded-full p-[3px] md:p-2"
                            onClick={toggleModal}
                          >
                            <span className="text-sm">{selectedCountry.emoji}</span>
                            <span className="text-sm">{selectedCountry.dialCode}</span>
                            <ArrowDropdown />
                          </button>
                          <input
                            className="text-sm w-full rounded-lg focus:outline-none text-navy-900"
                            type="text"
                            value={watch('phoneData.phoneNumber')}
                            placeholder={t('common.phonePlaceHolder')}
                            maxLength={15}
                            onChange={(e) => onChangePhoneNumber(e)}
                          />
                        </div>
                        {errors.phoneData?.phoneNumber && (
                          <p className="mt-1 ml-3 text-sm text-red-500">{errors.phoneData.phoneNumber.message}</p>
                        )}
                      </div>
                    )}
                  />
                </div>
              </div>
            </div>
            <div className="flex flex-col md:flex-row gap-y-6 md:gap-x-4">
              <TextInput
                className="!flex-1"
                placeholder={t('common.emailPlaceHolder')}
                labelClassName="!text-navy-900"
                label={t('common.email')}
                error={errors?.email?.message}
                // Locked on the email-first landing: the address is the one the link proved, and the
                // server rejects any other. Editable only in the claim flow (no finalize token).
                readOnly={!!finalizeToken}
                {...register('email')}
              />
              {!finalizeToken && (
                <TextInput
                  className="!flex-1"
                  placeholder={t('common.confirmEmailPlaceHolder')}
                  labelClassName="!text-navy-900"
                  label={t('common.confirmEmail')}
                  onPaste={(e) => e.preventDefault()}
                  error={errors?.confirmEmail?.message}
                  {...register('confirmEmail')}
                />
              )}
            </div>
            <div className="flex flex-col md:flex-row gap-y-6 md:gap-x-4">
              <div className="flex flex-col flex-1 gap-y-2">
                <span className="text-navy-900 text-sm font-bold">{t('common.gender')}</span>
                <div className="rounded-full border h-[54px] border-navy-900 border-opacity-20 flex">
                  <button
                    className={`flex-1 rounded-full  ${
                      gender === Gender.MALE ? 'bg-primary-500 text-white' : 'text-navy-900'
                    } text-[13px] font-medium`}
                    onClick={() => setGender(Gender.MALE)}
                  >
                    {t('common.men')}
                  </button>
                  <button
                    className={`flex-1 rounded-full  ${
                      gender === Gender.FEMALE ? 'bg-primary-500 text-white' : 'text-navy-900'
                    } text-[13px] font-medium`}
                    onClick={() => setGender(Gender.FEMALE)}
                  >
                    {t('common.women')}
                  </button>
                </div>
              </div>
              <Controller
                name="address"
                control={control}
                render={({ field }) => (
                  <AutocompleteLocationInput
                    {...field}
                    regionsOnly
                    label={t('common.location')}
                    placeholder={t('common.location')}
                    containerClassName="!flex-1"
                    inputClassName={errors?.address && '!border-boldOrange'}
                    labelClassName="!text-navy-900"
                    setValue={(location) =>
                      setValue('address', location, {
                        shouldDirty: true,
                        shouldValidate: true,
                      })
                    }
                    error={errors.address?.description?.message}
                  />
                )}
              />
            </div>

            <div className="flex items-start gap-x-2">
              <input
                type="checkbox"
                className="w-5 h-5 flex-shrink-0 cursor-pointer rounded border-navy-900/20 text-primary-500 focus:ring-primary-500"
                checked={watch('marketingOptIn')}
                onChange={(e) => {
                  setValue('marketingOptIn', e.target.checked)
                }}
              />
              <p className="text-sm text-navy-900">{t('common.compliantText')}</p>
            </div>

            <div className="flex items-start gap-x-2">
              <input
                id="termsOfService"
                type="checkbox"
                className="h-5 w-5 flex-shrink-0 cursor-pointer rounded border-navy-900/20 text-primary-500 focus:ring-primary-500"
                {...register('termsOfService')}
                aria-describedby="terms-error"
              />
              <div className="text-sm">
                <label htmlFor="termsOfService" className="text-navy-900">
                  {t('auth.iAgreeToThe')}{' '}
                  <a
                    href="https://www.dupr.com/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-primary-500 hover:underline"
                  >
                    {t('auth.termsOfService')}
                  </a>
                  {', '}
                  <a
                    href="https://www.dupr.com/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-primary-500 hover:underline"
                  >
                    {t('auth.privacyPolicy')}{' '}
                  </a>
                  {t('common.and')}{' '}
                  <a
                    href="https://www.dupr.com/cookie-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-primary-500 hover:underline"
                  >
                    {t('auth.cookiePolicy')}
                  </a>
                </label>
                {errors.termsOfService && (
                  <p id="terms-error" className="mt-1 text-sm text-red-500">
                    {errors.termsOfService.message}
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-start gap-x-2">
              <input
                id="phoneOptIn"
                type="checkbox"
                className="h-5 w-5 flex-shrink-0 cursor-pointer rounded border-navy-900/20 text-primary-500 focus:ring-primary-500"
                {...register('phoneOptIn')}
                aria-describedby="phoneOptIn-error"
              />
              <div className="text-sm">
                <label htmlFor="phoneOptIn" className="text-navy-900">
                  {t('common.phoneVerify')}
                </label>
                {errors.phoneOptIn && (
                  <p id="phoneOptIn-error" className="mt-3 text-sm text-red-500">
                    {t('common.phoneOptInRequired')}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-center items-center">
            <ButtonPrimary
              title={t('common.continue')}
              className="w-60 !py-[14px]"
              disabled={
                !watch('firstName') ||
                !watch('lastName') ||
                !watch('birthDate') ||
                !watch('email') ||
                !watch('confirmEmail') ||
                // Not gated on the location: typing a place without picking a suggestion leaves it
                // unset, and a disabled button gave no hint why. Validation names the field instead.
                !watch('termsOfService') ||
                (!ageVerified && !!ageError)
              }
              onClick={onSubmit}
            />
          </div>
        </div>
      </div>
      <SelectCountryModal
        open={isOpen}
        onClose={() => setIsOpen(false)}
        onSubmit={onChangeCountry}
        isoCode={watch(`phoneData.isoCode`)}
        dialCode={watch(`phoneData.dialCode`)}
      />
    </>
  )
}
